/**
 * The practice yard — where a brand-new survivor lands BEFORE the settlement.
 *
 * A trimmed sibling of `location-view.ts` (same sim, scene, camera and
 * controls) with a lesson plan on top: move, hit with a knife, shoot, search a
 * crate, put the loot on, then read how the map works. Everything here is
 * CLIENT-ONLY. The dummies, the crate and the weapons are local fiction: no
 * request leaves this file, and nothing touches the player's real inventory
 * (real combat and loot are server-authoritative, and a practice target has
 * neither stakes nor an opponent). The held weapon and the worn coat reach the
 * body through the animator's `AnimatorEquipmentSource` seam, the same one the
 * arena uses for the opponent.
 *
 * The lesson order and counts live in `tutorial-steps.ts`; this file only
 * reports what happened to it.
 */
import * as THREE from 'three';
import { DEFAULT_CAMERA } from '../types';
import type { Vec2 } from '../types';
import { createBaseSim } from '../sim/base-sim';
import { buildScene } from '../render/scene';
import { buildPlayerShadow } from '../render/player-shadow';
import { createPlayerAnimator, type AnimatorEquipmentSource } from '../render/character-animations';
import { createNameTags, playerDisplayName } from '../render/name-tags';
import { CameraController } from '../render/camera-controller';
import { Picker } from '../render/picking';
import { createAimArc } from '../render/aim-arc';
import { createMuzzleFlashPool } from '../render/sprite-fx';
import { createFloatingNumbers } from '../render/floating-numbers';
import { createPlayerControls } from '../player-controls';
import { keyName, trackHeldKeys } from '../key-input';
import { injectStyles } from '../ui/styles';
import { isFireKey } from '../../state/control-settings';
import { authStore } from '../../../core/state/auth-store';
import type { EquippedItemSummary } from '../../state/game-state.types';
import { firstHit, PRACTICE_AMMO, PRACTICE_WEAPONS, type PracticeWeaponKey, type PracticeTarget } from './tutorial-combat';
import {
  advance, currentStep, isFinished, START, TUTORIAL_STEPS, type TutorialEvent, type TutorialProgress,
} from './tutorial-steps';
import { MAP_SLIDES } from './tutorial-map-copy';
import {
  buildTutorialWorld, CRATE_ID, CRATE_POS, DUMMY_RADIUS, MARKER_RADIUS, MELEE_DUMMY, MELEE_DUMMY_ID,
  MOVE_MARKER, SHOOT_TARGETS, shootTargetId,
} from './tutorial-world';

/** Metres up from the feet where a hit number and a flash appear. */
const CHEST_HEIGHT = 1.15;
const MUZZLE_FORWARD = 0.55;

const COAT_KEY = 'torso_raincoat';

function summary(
  key: string, name: string, kind: string, slot: string, ammoType: string | null,
): EquippedItemSummary {
  return {
    id: `practice-${key}`,
    itemId: key,
    def: {
      key, name, slot: slot as never, kind, rarity: 'COMMON', armorClass: null, armorPoints: null,
      iconPath: null, baseDamage: null, fixedDamage: null, ammoType, maxDurability: 100,
    },
    durability: 100, maxDurability: 100, broken: false,
  };
}

/** Where the lesson wants the player to look or walk right now. */
function objectiveAt(progress: TutorialProgress): Vec2 | null {
  const step = currentStep(progress);
  if (!step) return null;
  switch (step.id) {
    case 'move': return MOVE_MARKER;
    case 'melee': return MELEE_DUMMY;
    case 'shoot': return SHOOT_TARGETS[Math.min(progress.done, SHOOT_TARGETS.length - 1)] ?? null;
    case 'loot': return CRATE_POS;
    default: return null;
  }
}

export function startTutorialView(
  root: HTMLElement,
  playerModel: THREE.Object3D | undefined,
  onDone: () => void,
): () => void {
  const cleanups: Array<() => void> = [];
  const removeStyles = injectStyles();
  let torndown = false;

  const world = buildTutorialWorld();
  const targets: PracticeTarget[] = [
    { id: MELEE_DUMMY_ID, position: MELEE_DUMMY, radius: DUMMY_RADIUS },
    ...SHOOT_TARGETS.map((p, i) => ({ id: shootTargetId(i), position: p, radius: DUMMY_RADIUS })),
  ];

  root.innerHTML = '';
  const container = document.createElement('div');
  container.className = 'bv-root';
  root.appendChild(container);

  const renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(container.clientWidth || 1, container.clientHeight || 1);
  container.appendChild(renderer.domElement);

  const sim = createBaseSim(world);
  const sceneHandles = buildScene(world, playerModel);

  // ---- what the body holds and wears: local state behind the animator's seam ----
  let held: PracticeWeaponKey = 'hunting_knife';
  let coatLooted = false;
  let coatWorn = false;
  let ammo = PRACTICE_AMMO;
  const listeners = new Set<() => void>();
  const notifyBody = (): void => { for (const fn of listeners) fn(); };
  const bodySource: AnimatorEquipmentSource = {
    slots: () => ({
      PRIMARY: held === 'magnum'
        ? summary('magnum', 'Magnum', 'FIREARM', 'PRIMARY', 'pistol')
        : summary('hunting_knife', 'Hunting knife', 'MELEE', 'PRIMARY', null),
      ...(coatWorn ? { TORSO: summary(COAT_KEY, 'Raincoat', 'ARMOUR', 'TORSO', null) } : {}),
    }),
    subscribe: (fn) => { listeners.add(fn); return () => listeners.delete(fn); },
  };
  const playerAnimator = createPlayerAnimator(sceneHandles.player, bodySource);
  cleanups.push(() => playerAnimator.dispose());

  const cam = new CameraController(aspectOf(container), { ...DEFAULT_CAMERA, ...world.camera });
  const picker = new Picker();
  let panelOpen = false;
  const controls = createPlayerControls({
    container, canvas: renderer.domElement, picker, getCamera: () => cam.camera,
    isInputBlocked: () => panelOpen,
  });
  cleanups.push(() => controls.dispose());
  const reducedMotionMql = window.matchMedia('(prefers-reduced-motion: reduce)');

  const playerShadow = buildPlayerShadow();
  sceneHandles.scene.add(playerShadow);
  const aimArc = createAimArc();
  sceneHandles.scene.add(aimArc.object);
  cleanups.push(() => aimArc.dispose());
  const muzzleFlashes = createMuzzleFlashPool();
  sceneHandles.scene.add(muzzleFlashes.object);
  cleanups.push(() => muzzleFlashes.dispose());
  const floaters = createFloatingNumbers();
  sceneHandles.scene.add(floaters.object);
  cleanups.push(() => floaters.dispose());
  const nameTags = createNameTags(world);
  sceneHandles.scene.add(nameTags.object);
  const unsubName = authStore.subscribe(() => {
    nameTags.setPlayerName(playerDisplayName(authStore.get().user));
  });
  cleanups.push(unsubName, () => nameTags.dispose());

  // ---- lesson state ----
  let progress: TutorialProgress = START;
  const report = (e: TutorialEvent): void => {
    const before = progress;
    progress = advance(progress, e);
    if (progress !== before) renderCoach();
  };

  // ---- DOM ----
  const el = (cls: string, tag = 'div'): HTMLElement => {
    const n = document.createElement(tag);
    n.className = cls;
    return n;
  };
  const coach = el('bv-tm-coach');
  const hud = el('bv-tm-hud');
  const guide = el('bv-tm-guide');
  const toast = el('bv-tm-toast');
  toast.hidden = true;
  const gear = el('bv-tm-gear');
  gear.hidden = true;
  const modal = el('bv-tm-modal');
  modal.hidden = true;
  container.append(guide, coach, hud, toast, gear, modal);

  let toastTimer = 0;
  const showToast = (m: string): void => {
    window.clearTimeout(toastTimer);
    toast.textContent = m;
    toast.hidden = false;
    toastTimer = window.setTimeout(() => { toast.hidden = true; }, 3000);
  };

  const finish = (): void => { if (!torndown) onDone(); };

  function renderCoach(): void {
    coach.replaceChildren();
    if (isFinished(progress)) {
      const h = el('bv-tm-title', 'h2'); h.textContent = 'Practice complete';
      const p = el('bv-tm-goal', 'p'); p.textContent = 'You are ready. Go to the settlement.';
      const b = el('bv-tm-btn bv-tm-primary', 'button') as HTMLButtonElement;
      b.type = 'button'; b.textContent = 'Enter the settlement';
      b.addEventListener('click', finish);
      coach.append(h, p, b);
      return;
    }
    const step = currentStep(progress)!;
    const kicker = el('bv-tm-kicker');
    kicker.textContent = `Step ${progress.index + 1} of ${TUTORIAL_STEPS.length}`;
    const title = el('bv-tm-title', 'h2'); title.textContent = step.title;
    const goal = el('bv-tm-goal', 'p'); goal.textContent = step.goal;
    const dots = el('bv-tm-dots');
    TUTORIAL_STEPS.forEach((_, i) => {
      const d = el('bv-tm-dot');
      if (i < progress.index) d.classList.add('bv-tm-dot-done');
      if (i === progress.index) d.classList.add('bv-tm-dot-now');
      dots.appendChild(d);
    });
    const tips = el('bv-tm-tips', 'ul');
    for (const t of step.tips) { const li = document.createElement('li'); li.textContent = t; tips.appendChild(li); }
    const row = el('bv-tm-row');
    if (step.count > 1) {
      const c = el('bv-tm-count'); c.textContent = `${progress.done} / ${step.count}`;
      row.appendChild(c);
    }
    const skip = el('bv-tm-link', 'button') as HTMLButtonElement;
    skip.type = 'button'; skip.textContent = 'Skip practice';
    skip.addEventListener('click', finish);
    row.appendChild(skip);
    coach.append(kicker, title, goal, dots, tips, row);
    if (step.id === 'equip') openGearHint();
    if (step.id === 'map') {
      const b = el('bv-tm-btn bv-tm-primary', 'button') as HTMLButtonElement;
      b.type = 'button'; b.textContent = 'Open the map guide';
      b.addEventListener('click', openMapGuide);
      coach.appendChild(b);
    }
  }

  function renderHud(): void {
    hud.replaceChildren();
    for (const [i, key] of (['hunting_knife', 'magnum'] as PracticeWeaponKey[]).entries()) {
      const w = PRACTICE_WEAPONS[key];
      const b = el('bv-tm-slot', 'button') as HTMLButtonElement;
      b.type = 'button';
      if (held === key) b.classList.add('bv-tm-slot-on');
      const k = el('bv-tm-key'); k.textContent = String(i + 1);
      const n = el('bv-tm-slotname'); n.textContent = w.name;
      b.append(k, n);
      if (w.usesAmmo) { const a = el('bv-tm-ammo'); a.textContent = `${ammo}`; b.appendChild(a); }
      b.addEventListener('click', () => select(key));
      hud.appendChild(b);
    }
    const g = el('bv-tm-slot', 'button') as HTMLButtonElement;
    g.type = 'button';
    const gk = el('bv-tm-key'); gk.textContent = 'I';
    const gn = el('bv-tm-slotname'); gn.textContent = 'Gear';
    g.append(gk, gn);
    if (coatLooted && !coatWorn) g.classList.add('bv-tm-slot-pulse');
    g.addEventListener('click', toggleGear);
    hud.appendChild(g);
  }

  function select(key: PracticeWeaponKey): void {
    if (held === key) return;
    held = key;
    notifyBody();
    renderHud();
  }

  // ---- gear window (practice items only) ----
  function renderGear(): void {
    gear.replaceChildren();
    const h = el('bv-tm-title', 'h2'); h.textContent = 'Gear';
    const note = el('bv-tm-note', 'p'); note.textContent = 'Practice items. They stay in the yard.';
    gear.append(h, note);
    if (!coatLooted) {
      const p = el('bv-tm-goal', 'p'); p.textContent = 'You have nothing to wear yet. Search the crate.';
      gear.appendChild(p);
    } else {
      const row = el('bv-tm-item');
      const name = el('bv-tm-itemname'); name.textContent = 'Raincoat';
      const b = el('bv-tm-btn bv-tm-primary', 'button') as HTMLButtonElement;
      b.type = 'button'; b.textContent = coatWorn ? 'Take off' : 'Wear';
      b.addEventListener('click', () => {
        coatWorn = !coatWorn;
        notifyBody();
        renderGear(); renderHud();
        if (coatWorn) report({ type: 'equipped' });
      });
      row.append(name, b);
      gear.appendChild(row);
    }
    const close = el('bv-tm-link', 'button') as HTMLButtonElement;
    close.type = 'button'; close.textContent = 'Close';
    close.addEventListener('click', toggleGear);
    gear.appendChild(close);
  }
  function toggleGear(): void {
    if (!modal.hidden) return;
    gear.hidden = !gear.hidden;
    panelOpen = !gear.hidden;
    if (!gear.hidden) renderGear();
  }
  function openGearHint(): void { /* the pulsing Gear button is the cue */ }

  // ---- the map guide ----
  let slide = 0;
  function renderSlide(): void {
    modal.replaceChildren();
    const s = MAP_SLIDES[slide]!;
    const card = el('bv-tm-card');
    const badge = el('bv-tm-badge'); badge.textContent = s.badge;
    const kick = el('bv-tm-kicker'); kick.textContent = `${slide + 1} of ${MAP_SLIDES.length}`;
    const title = el('bv-tm-title', 'h2'); title.textContent = s.title;
    const lines = el('bv-tm-lines', 'ul');
    for (const l of s.lines) { const li = document.createElement('li'); li.textContent = l; lines.appendChild(li); }
    const nav = el('bv-tm-row');
    const back = el('bv-tm-btn', 'button') as HTMLButtonElement;
    back.type = 'button'; back.textContent = 'Back'; back.disabled = slide === 0;
    back.addEventListener('click', () => { slide -= 1; renderSlide(); });
    const last = slide === MAP_SLIDES.length - 1;
    const next = el('bv-tm-btn bv-tm-primary', 'button') as HTMLButtonElement;
    next.type = 'button'; next.textContent = last ? 'Done' : 'Next';
    next.addEventListener('click', () => {
      if (!last) { slide += 1; renderSlide(); return; }
      modal.hidden = true; panelOpen = false;
      report({ type: 'map-read' });
    });
    nav.append(back, next);
    card.append(badge, kick, title, lines, nav);
    modal.appendChild(card);
  }
  function openMapGuide(): void {
    gear.hidden = true;
    slide = 0;
    modal.hidden = false;
    panelOpen = true;
    renderSlide();
  }

  // ---- combat ----
  let nextAttackAt = 0;
  let reloadTimer = 0;
  function attack(): void {
    const now = performance.now();
    if (now < nextAttackAt || panelOpen) return;
    const w = PRACTICE_WEAPONS[held];
    if (w.usesAmmo && ammo <= 0) {
      showToast('Out of bullets. Reloading...');
      nextAttackAt = now + 1300;
      reloadTimer = window.setTimeout(() => { ammo = PRACTICE_AMMO; renderHud(); }, 1200);
      return;
    }
    nextAttackAt = now + w.cooldownMs;
    const snap = sim.getSnapshot();
    const facing = snap.player.facing;
    playerAnimator.playAttack();
    if (w.usesAmmo) {
      ammo -= 1;
      renderHud();
      const dir = new THREE.Vector3(Math.sin(facing), 0, Math.cos(facing));
      muzzleFlashes.flash(
        new THREE.Vector3(snap.player.position.x, CHEST_HEIGHT, snap.player.position.z).addScaledVector(dir, MUZZLE_FORWARD),
        dir,
      );
    }
    const hit = firstHit(snap.player.position, facing, w, targets);
    if (!hit) return;
    floaters.spawn({
      text: `-${w.damage}`, tone: 'damage-dealt', emphasis: false,
      x: hit.position.x, y: CHEST_HEIGHT + 0.4, z: hit.position.z,
    });
    // Only the target the CURRENT step is about counts, so a stray knife jab
    // at a shooting target does not finish the wrong lesson.
    const step = currentStep(progress)?.id;
    if (w.kind === 'MELEE' && step === 'melee' && hit.id === MELEE_DUMMY_ID) report({ type: 'melee-hit' });
    if (w.kind === 'FIREARM' && step === 'shoot' && hit.id !== MELEE_DUMMY_ID) report({ type: 'shot-hit' });
  }

  // ---- loot ----
  let crateOpen = false;
  const trySearch = (): void => {
    if (crateOpen || panelOpen) return;
    const near = sim.getSnapshot().nearestInteractable;
    if (near?.lootObjectId !== CRATE_ID) return;
    crateOpen = true;
    coatLooted = true;
    const obj = world.objects.find((o) => o.id === CRATE_ID);
    if (obj) obj.interactable = false;
    showToast('You found: Raincoat');
    renderHud();
    report({ type: 'looted' });
  };

  // ---- input ----
  const keys = new Set<string>();
  let hoverId: string | null = null;
  let dragging = false;
  let dragMoved = 0;
  let lastPointerX = 0;

  const onKeyDown = (e: KeyboardEvent): void => {
    if (panelOpen && e.key !== 'Escape' && keyName(e) !== 'i') return;
    if (isFireKey(e)) {
      e.preventDefault();
      if (controls.aiming) attack();
      return;
    }
    const k = keyName(e);
    if (k === '1') select('hunting_knife');
    else if (k === '2') select('magnum');
    else if (k === 'i') toggleGear();
    else if (k === 'e') trySearch();
    else if (k === 'escape' && !gear.hidden) toggleGear();
    keys.add(k);
    controls.handleKeyDown(e);
  };
  const onKeyUp = (e: KeyboardEvent): void => {
    keys.delete(keyName(e));
    controls.handleKeyUp(e);
  };
  const onPointerDown = (e: PointerEvent): void => {
    if (e.button !== 0 || e.target !== renderer.domElement) return;
    // LMB while aiming is the attack button; it must not also start a camera drag.
    if (controls.aiming) { attack(); return; }
    dragging = true;
    dragMoved = 0;
    lastPointerX = e.clientX;
    container.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: PointerEvent): void => {
    hoverId = picker.pick(e.clientX, e.clientY, container, cam.camera, sceneHandles.pickables);
    if (dragging) {
      const dx = e.clientX - lastPointerX;
      lastPointerX = e.clientX;
      dragMoved += Math.abs(dx);
      cam.rotate(dx);
    }
  };
  const onPointerUp = (e: PointerEvent): void => {
    if (dragging && dragMoved < 5) {
      const near = sim.getSnapshot().nearestInteractable;
      if (hoverId && near && near.objectId === hoverId) trySearch();
    }
    dragging = false;
    if (container.hasPointerCapture(e.pointerId)) container.releasePointerCapture(e.pointerId);
  };
  const onWheel = (e: WheelEvent): void => { e.preventDefault(); cam.zoom(Math.sign(e.deltaY)); };
  container.addEventListener('pointerdown', onPointerDown);
  container.addEventListener('pointermove', onPointerMove);
  container.addEventListener('pointerup', onPointerUp);
  container.addEventListener('wheel', onWheel, { passive: false });
  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);
  // Held keys are dropped when their keyup can be lost — see key-input.ts.
  cleanups.push(trackHeldKeys(keys));
  cleanups.push(
    () => container.removeEventListener('pointerdown', onPointerDown),
    () => container.removeEventListener('pointermove', onPointerMove),
    () => container.removeEventListener('pointerup', onPointerUp),
    () => container.removeEventListener('wheel', onWheel),
    () => window.removeEventListener('keydown', onKeyDown),
    () => window.removeEventListener('keyup', onKeyUp),
  );

  const resize = (): void => {
    const w = container.clientWidth || 1;
    const h = container.clientHeight || 1;
    renderer.setSize(w, h, false);
    cam.setAspect(w / h);
  };
  const ro = new ResizeObserver(resize);
  ro.observe(container);
  cleanups.push(() => ro.disconnect());
  resize();

  // ---- the objective cue: a marker over the target, an edge arrow when off-screen ----
  const cueVec = new THREE.Vector3();
  const updateGuide = (): void => {
    const at = objectiveAt(progress);
    if (!at || panelOpen) { guide.hidden = true; return; }
    cueVec.set(at.x, 1.9, at.z).project(cam.camera);
    const behind = cueVec.z > 1;
    let x = cueVec.x; let y = cueVec.y;
    if (behind) { x = -x; y = -y; }
    const off = behind || Math.abs(x) > 0.92 || Math.abs(y) > 0.88;
    const cx = Math.max(-0.92, Math.min(0.92, x));
    const cy = Math.max(-0.88, Math.min(0.88, y));
    guide.hidden = false;
    guide.classList.toggle('bv-tm-guide-edge', off);
    guide.style.left = `${((cx + 1) / 2) * 100}%`;
    guide.style.top = `${((1 - cy) / 2) * 100}%`;
    guide.style.transform = off
      ? `translate(-50%, -50%) rotate(${Math.atan2(-(y), x) + Math.PI / 2}rad)`
      : 'translate(-50%, -100%)';
  };

  let raf = 0;
  let last = performance.now();
  const frame = (now: number): void => {
    raf = requestAnimationFrame(frame);
    const dt = Math.min((now - last) / 1000, 0.05);
    last = now;

    controls.updateAim(sim.getSnapshot().player.position);
    const input = {
      moveX: (keys.has('d') || keys.has('arrowright') ? 1 : 0) - (keys.has('a') || keys.has('arrowleft') ? 1 : 0),
      moveZ: (keys.has('w') || keys.has('arrowup') ? 1 : 0) - (keys.has('s') || keys.has('arrowdown') ? 1 : 0),
      aimYaw: controls.simAimYaw,
      gait: controls.gait,
      crouched: controls.crouched,
    };
    sim.update(dt, panelOpen ? { ...input, moveX: 0, moveZ: 0 } : input, cam.yawValue);

    const snap = sim.getSnapshot();
    sceneHandles.player.position.set(snap.player.position.x, 0, snap.player.position.z);
    sceneHandles.player.rotation.y = snap.player.facing;
    playerShadow.position.set(snap.player.position.x, playerShadow.position.y, snap.player.position.z);
    playerShadow.rotation.y = snap.player.facing;
    cam.update(snap.player.position);
    playerAnimator.update(dt, {
      moving: snap.player.moving, gait: controls.gait, crouched: controls.crouched,
      aiming: controls.aiming, dialogOpen: false,
    });

    const w = PRACTICE_WEAPONS[held];
    aimArc.update({
      visible: controls.aiming,
      arc: { kind: w.kind, arcLength: w.arcLength, arcWidth: w.arcWidth, itemKey: w.key },
      x: snap.player.position.x, z: snap.player.position.z, facing: snap.player.facing,
    });

    if (currentStep(progress)?.id === 'move'
      && Math.hypot(snap.player.position.x - MOVE_MARKER.x, snap.player.position.z - MOVE_MARKER.z) <= MARKER_RADIUS) {
      report({ type: 'reached-marker' });
    }

    const inRange = !!snap.nearestInteractable && snap.nearestInteractable.objectId === hoverId;
    sceneHandles.setHovered(hoverId, inRange);
    sceneHandles.updateAmbience(dt, reducedMotionMql.matches);
    sceneHandles.updateGrass(dt, cam.yawValue);
    sceneHandles.spriteSpawns.update(dt);
    nameTags.update(snap.player.position, snap.actors);
    floaters.update(dt, cam.camera);
    muzzleFlashes.update(dt);
    updateGuide();

    renderer.render(sceneHandles.scene, cam.camera);
  };
  renderCoach();
  renderHud();
  raf = requestAnimationFrame(frame);
  // DEV inspection hook, same gate as arena-view's window.__duel.
  if (import.meta.env.DEV) {
    (window as unknown as Record<string, unknown>).__yard = {
      scene: sceneHandles.scene, camera: cam.camera, player: sceneHandles.player, renderer, sim,
    };
  }

  return () => {
    torndown = true;
    window.clearTimeout(toastTimer);
    window.clearTimeout(reloadTimer);
    cancelAnimationFrame(raf);
    for (const fn of cleanups) fn();
    sceneHandles.dispose();
    renderer.dispose();
    renderer.forceContextLoss();
    renderer.domElement.remove();
    removeStyles();
    container.remove();
  };
}

function aspectOf(el: HTMLElement): number {
  return (el.clientWidth || 1) / (el.clientHeight || 1);
}
